import {
  type ComputedRef,
  computed,
  onBeforeUnmount,
  onMounted,
  proxyRefs,
  type ShallowRef,
  shallowRef,
} from 'vue';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { idleWatcher } from '@/services/IdleWatcher';
import { session } from '@/services/Session';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import {
  CODE_PATTERN,
  classifyInput,
  extractCode,
  STRICT_GOPHOTO_URL,
  validateCode,
} from '../gophoto-scan-parser.js';
import {
  trackBufferedInput,
  trackClear,
  trackExtractedButInvalid,
  trackExtraction,
  trackInputClassified,
  trackInvalidCode,
  trackLooseMatch,
  trackNavigation,
  trackNoInput,
  trackRegistrationFailure,
  trackRegistrationSuccess,
  trackRejectedUrl,
  trackScanMounted,
  trackSubmissionError,
  trackSubmitStarted,
  trackUnrecognizedFormat,
  withRegistrationSpan,
} from './gophotoScanTelemetry';
import {
  bufferScanInput,
  displayedScanCode,
  manualCodeCandidate,
  normalizeScanInput,
} from './scanInputBuffer';

const AUTO_SUBMIT_DELAY_MS = 500;
const NO_INPUT_TIMEOUT_MS = 30_000;

interface IdleWatcherPause {
  continue: () => void;
}

interface UseGoPhotoScanFlowOptions {
  focusInput: () => void;
  startFocusManagement: () => void;
  stopFocusManagement: () => void;
  translate: (key: string, fallback: string) => string;
}

export interface GoPhotoScanPublicApi {
  code: string;
  readonly displayCode: string;
  error: string;
  isLoading: boolean;
  mountedAt: number | null;
  rawInput: string;
  scanAttemptCount: number;
  readonly store: typeof store;
  clear: () => void;
  focusInput: () => void;
  handleInput: (value?: string) => void;
  handleKeydown: (event: KeyboardEvent) => void;
  navigateToNextStep: () => void;
  parseGophotoCode: (input: unknown) => string | null;
  submit: () => Promise<void>;
}

interface UseGoPhotoScanFlowResult {
  code: ShallowRef<string>;
  displayCode: ComputedRef<string>;
  error: ShallowRef<string>;
  handleInput: (value?: string) => void;
  handleKeydown: (event: KeyboardEvent) => void;
  isLoading: ShallowRef<boolean>;
  publicApi: GoPhotoScanPublicApi;
  rawInput: ShallowRef<string>;
}

export function useGoPhotoScanFlow({
  focusInput,
  startFocusManagement,
  stopFocusManagement,
  translate,
}: UseGoPhotoScanFlowOptions): UseGoPhotoScanFlowResult {
  const rawInput = shallowRef('');
  const code = shallowRef('');
  const error = shallowRef('');
  const isLoading = shallowRef(false);
  const mountedAt = shallowRef<number | null>(null);
  const scanAttemptCount = shallowRef(0);
  const displayCode = computed(() =>
    displayedScanCode(code.value, rawInput.value)
  );

  let idleWatcherPause: IdleWatcherPause | null = null;
  let autoSubmitTimer: ReturnType<typeof setTimeout> | null = null;
  let noInputTimer: ReturnType<typeof setTimeout> | null = null;
  let isMounted = true;
  let isProcessingInput = false;
  let receivedAnyInput = false;

  function cancelAutoSubmit(): void {
    if (autoSubmitTimer) {
      clearTimeout(autoSubmitTimer);
      autoSubmitTimer = null;
    }
  }

  function scheduleFinalizedInput(input: string): void {
    cancelAutoSubmit();
    autoSubmitTimer = setTimeout(() => {
      autoSubmitTimer = null;
      processFinalizedInput(input);
    }, AUTO_SUBMIT_DELAY_MS);
  }

  function processBufferedInput({ force = false } = {}): void {
    if (isLoading.value || !isMounted) {
      return;
    }

    const buffer = bufferScanInput(rawInput.value, { force });

    if (buffer.status === 'empty') {
      cancelAutoSubmit();
      code.value = '';
      return;
    }

    if (buffer.status === 'pending') {
      cancelAutoSubmit();
      code.value = '';
      trackBufferedInput(buffer);
      return;
    }

    if (force) {
      processFinalizedInput(buffer.input);
      return;
    }

    scheduleFinalizedInput(buffer.input);
  }

  function showScanError(key: string, fallback: string): void {
    error.value = translate(key, fallback);
    rawInput.value = '';
    code.value = '';
  }

  function rejectFinalizedUrl(
    input: string,
    classificationType: 'gophoto_url_malformed' | 'unknown_url'
  ): void {
    trackRejectedUrl(input, classificationType);
    showScanError(
      'GoPhotoScan.invalidQR',
      'Invalid QR code. Please try again.'
    );
  }

  function processFinalizedInput(input: string): void {
    if (!input) {
      return;
    }

    if (input.length >= 6) {
      scanAttemptCount.value += 1;
    }

    const classification = classifyInput(input);
    trackInputClassified(input, classification, scanAttemptCount.value);

    const extraction = extractCode(input, classification);
    trackExtraction(classification, extraction);

    if (extraction.strategy === 'loose_url') {
      trackLooseMatch(input, extraction.hostname);
    }

    if (extraction.code) {
      const validation = validateCode(extraction.code);
      if (validation.valid) {
        code.value = extraction.code;
        rawInput.value = extraction.code;
        void submit();
        return;
      }

      trackExtractedButInvalid(validation.reason);
      code.value = '';
      rawInput.value = '';
    } else if (
      classification.type === 'gophoto_url_malformed' ||
      classification.type === 'unknown_url'
    ) {
      rejectFinalizedUrl(input, classification.type);
      return;
    } else {
      const sanitized = manualCodeCandidate(input);
      code.value = sanitized;

      if (input.length >= 6 && sanitized.length !== 6) {
        trackUnrecognizedFormat(input, sanitized);
      }
    }

    void submit();
  }

  function handleInput(value?: string): void {
    if (typeof value === 'string') {
      rawInput.value = value;
    }
    if (isProcessingInput) {
      return;
    }

    isProcessingInput = true;
    try {
      receivedAnyInput = true;
      error.value = '';
      code.value = '';
      processBufferedInput();
    } finally {
      isProcessingInput = false;
    }
  }

  function handleKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Enter'];
    const isUrlCharacter = /^[A-Za-z0-9:/.\\_-]$/.test(event.key);

    if (!(isUrlCharacter || allowedKeys.includes(event.key))) {
      event.preventDefault();
    }

    if (event.key === 'Enter' && !isLoading.value) {
      event.preventDefault();
      cancelAutoSubmit();
      processBufferedInput({ force: true });
    }
  }

  function parseGophotoCode(input: unknown): string | null {
    if (!input) {
      return null;
    }

    const match = normalizeScanInput(input).match(STRICT_GOPHOTO_URL);
    return match?.[1]?.toUpperCase() ?? null;
  }

  async function submit(): Promise<void> {
    if (isLoading.value) {
      return;
    }

    isLoading.value = true;

    if (!CODE_PATTERN.test(code.value)) {
      trackInvalidCode(code.value);
      showScanError(
        'GoPhotoScan.invalidCode',
        'Invalid code format. Please scan again.'
      );
      isLoading.value = false;
      return;
    }

    const submitStartTime = performance.now();
    trackSubmitStarted(scanAttemptCount.value);

    try {
      const success = await withRegistrationSpan(scanAttemptCount.value, () =>
        session.setGophotoCode(code.value)
      );
      const submitDuration = performance.now() - submitStartTime;

      if (!isMounted) {
        return;
      }

      if (success) {
        const totalScanDuration = mountedAt.value
          ? performance.now() - mountedAt.value
          : null;
        trackRegistrationSuccess({
          attemptCount: scanAttemptCount.value,
          submitDuration,
          totalScanDuration,
        });
        navigateToNextStep();
      } else {
        trackRegistrationFailure(submitDuration);
        showScanError(
          'GoPhotoScan.failedToRegister',
          'Failed to register code. Please try again.'
        );
      }
    } catch (value) {
      // biome-ignore lint/suspicious/noConsole: preserve operational diagnostics.
      console.error('[GoPhotoScan] Error:', value);
      const submitDuration = performance.now() - submitStartTime;
      trackSubmissionError(
        value,
        scanAttemptCount.value,
        submitDuration,
        mountedAt.value ? Math.round(performance.now() - mountedAt.value) : null
      );
      showScanError(
        'GoPhotoScan.error',
        'An error occurred. Please try again.'
      );
    } finally {
      isLoading.value = false;
    }
  }

  function navigateToNextStep(): void {
    const requiresGdprConsent =
      store.kiosk?.show_privacy_popup ??
      Boolean(store.kiosk?.privacy_policy_variant);
    const nextStep = requiresGdprConsent ? 'gdpr-consent' : 'art-style';

    trackNavigation(requiresGdprConsent, nextStep);

    if (requiresGdprConsent) {
      const link = router.getLinkToStep('gdpr-consent', {
        query: { next: 'art-style' },
      });
      void router.redirectToPath(link);
      return;
    }

    void router.redirectToStep('art-style');
  }

  function clear(): void {
    trackClear(Boolean(error.value));
    cancelAutoSubmit();
    code.value = '';
    rawInput.value = '';
    error.value = '';
    focusInput();
  }

  const publicApi: GoPhotoScanPublicApi = proxyRefs({
    code,
    get displayCode(): string {
      return displayCode.value;
    },
    error,
    isLoading,
    mountedAt,
    rawInput,
    scanAttemptCount,
    store,
    clear,
    focusInput,
    handleInput,
    handleKeydown,
    navigateToNextStep,
    parseGophotoCode,
    submit,
  });

  onMounted(() => {
    isMounted = true;
    exposeToGlobalScope(publicApi, 'GoPhotoScan');
    mountedAt.value = performance.now();
    trackScanMounted(NO_INPUT_TIMEOUT_MS);
    idleWatcherPause = idleWatcher.pause();
    startFocusManagement();

    noInputTimer = setTimeout(() => {
      noInputTimer = null;
      if (!receivedAnyInput && isMounted) {
        trackNoInput(NO_INPUT_TIMEOUT_MS);
      }
    }, NO_INPUT_TIMEOUT_MS);
  });

  onBeforeUnmount(() => {
    isMounted = false;
    idleWatcherPause?.continue();
    idleWatcherPause = null;
    stopFocusManagement();
    cancelAutoSubmit();

    if (noInputTimer) {
      clearTimeout(noInputTimer);
      noInputTimer = null;
    }
  });

  return {
    code,
    displayCode,
    error,
    handleInput,
    handleKeydown,
    isLoading,
    publicApi,
    rawInput,
  };
}
